import { useNavigation } from '@react-navigation/native';
import { IpadLang, Lang, VisitAuthorizationStatusType } from '@w3lcome/types';
import { Button, Header, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { colors, fontSizes } from '_/config/theme';
import removeHtmlTags from '_/helpers/removeHtmlTags';
import { textReplace } from '_/helpers/textReplace';
import { VisitsModelCheckin } from '_/interfaces/VisitsModelCheckin';
import { useCustomization } from '_/hooks/CustomizationContext';
import { useIntegrations } from '_/hooks/IntegrationsContext';
import { usePrinters } from '_/hooks/PrintersContext';
import { useVisit } from '_/hooks/VisitContext';
import { AppRoute, HomeProps } from '_/navigation/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, ScrollView, StyleSheet } from 'react-native';

import CustomMessage from './CustomMessage';
import appConfig from '_/config/app';
import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';

const CheckinMessageScreen: React.FC = () => {
  const { customization } = useCustomization();

  const { visit, checkinVisit, isPrintVisitBadge, navigateToHome } = useVisit();
  const { printVisitBadge } = usePrinters();
  const { runCheckinIntegrations } = useIntegrations();
  const [startTimer, setStartTimer] = useState(false);
  const [createdVisit, setCreatedVisit] = useState<VisitsModelCheckin>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { t, i18n } = useTranslation();
  const navigation = useNavigation<HomeProps>();

  const { selectedOrFallbackCompany, selectedOrFallbackVisitFlowType } =
    useCentralizedValuesFallback();

  const ipadCheckinErrorMessage = selectedOrFallbackCompany?.visitsConfig?.ipadCheckinErrorMessage;

  useEffect(() => {
    async function checkin() {
      let value = null;

      try {
        value = await checkinVisit(visit);
        setCreatedVisit(value);
      } catch (error) {
        if (ipadCheckinErrorMessage && Object.keys(ipadCheckinErrorMessage).length > 0) {
          return navigation.navigate(AppRoute.CHECKIN_ERROR_SCREEN);
        }
        setErrorMessage(t('checkinScreen.checkinError'));
        setStartTimer(true);
        return;
      }

      if (value?.visitorStatus === 'BLOCKED') {
        if (ipadCheckinErrorMessage && Object.keys(ipadCheckinErrorMessage).length > 0) {
          return navigation.navigate(AppRoute.CHECKIN_ERROR_SCREEN);
        }
        setErrorMessage(t('checkinScreen.checkinError'));
        setStartTimer(true);
        return;
      }

      runCheckinIntegrations(visit);

      if (isPrintVisitBadge && appConfig.companyIdSbt !== selectedOrFallbackCompany?.id) {
        const printPayload = { ...visit, ...value, checkinAt: value?.checkinAt || new Date() };

        const shouldPrintWithCard =
          !selectedOrFallbackVisitFlowType?.allowPrintingWithoutCardNumber;

        if (shouldPrintWithCard && visit?.cardNumber) {
          printVisitBadge(printPayload).catch(() => {});
        } else {
          printVisitBadge(printPayload, {
            throwOnError: true,
            customConfig: selectedOrFallbackVisitFlowType?.printerXML,
          }).catch((error) => {
            if (selectedOrFallbackVisitFlowType?.allowPrintingWithoutCardNumber) {
              navigation.navigate(AppRoute.CHECKIN_ERROR_SCREEN);
            }
          });
        }
      }

      setStartTimer(true);
    }

    checkin();
  }, []);

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    if (startTimer && !selectedOrFallbackVisitFlowType?.extraSuccessScreenEnabled) {
      timerId = setTimeout(() => {
        navigateToHome(navigation);
      }, (customization?.ipadWelcomeDuration || 6) * 1000);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [startTimer]);

  const handleContinue = () => {
    navigation.navigate(AppRoute.EXTRA_PRINT_SCREENS, {
      createdVisit,
    });
  };

  const getWelcomeMessageByLang = (
    messageKey: 'ipadWelcomeMessage' | 'ipadWelcomeAwaitingAuthorizationMessage'
  ) => {
    const message = customization?.[messageKey];
    if (!message) return '';

    if (typeof message === 'string') return message;

    const langValue =
      message[i18n.language as Lang] ||
      message[Lang.EN] ||
      message[Object.keys(message)[0] as Lang];

    return (langValue || '').trim();
  };

  const getWelcomeMessage = useCallback(() => {
    const { authorizationEnabled } = customization || {};

    const hasMessagePreRegistrationAwaitingAuthorized = !!removeHtmlTags(
      getWelcomeMessageByLang('ipadWelcomeAwaitingAuthorizationMessage') ?? ''
    );

    if (hasMessagePreRegistrationAwaitingAuthorized && authorizationEnabled) {
      if (visit.meeting) {
        return visit.authorizationStatus === VisitAuthorizationStatusType.AWAITING
          ? getWelcomeMessageByLang('ipadWelcomeAwaitingAuthorizationMessage')
          : getWelcomeMessageByLang('ipadWelcomeMessage');
      }

      return getWelcomeMessageByLang('ipadWelcomeAwaitingAuthorizationMessage');
    }

    return getWelcomeMessageByLang('ipadWelcomeMessage');
  }, [customization, visit]);

  const ipadWelcomeMessage = useMemo(() => {
    const welcomeMessage = getWelcomeMessage();

    return textReplace(
      welcomeMessage || '',
      i18n.language as IpadLang,
      customization,
      createdVisit || visit
    );
  }, [customization, visit, createdVisit]);

  return (
    <View style={styles.container}>
      <ContainerWidthLimit>
        <Header hideReturnButton />
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
        >
          {!errorMessage && selectedOrFallbackVisitFlowType?.displayWelcomeMessage && (
            <View style={{ alignItems: 'center' }}>
              <>
                <Text size={fontSizes.xxxl}>{t('Welcome')}</Text>
                <Text fontType="bold" size={fontSizes.xxxl}>
                  {visit.name}
                </Text>
              </>
            </View>
          )}
          <CustomMessage
            error={errorMessage}
            message={ipadWelcomeMessage}
            loading={!errorMessage && !createdVisit}
          />
        </ScrollView>
        {startTimer && selectedOrFallbackVisitFlowType?.extraSuccessScreenEnabled && (
          <Button
            onPress={handleContinue}
            containerStyles={styles.buttonContainer}
            buttonStyles={styles.button}
          >
            <Text fontType="bold" size={fontSizes.lg} color={colors.white}>
              {t('checkinScreen.continue', { defaultValue: 'Continuar' })}
            </Text>
          </Button>
        )}
      </ContainerWidthLimit>
    </View>
  );
};

export default CheckinMessageScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    alignItems: 'center',
  },
  scroll: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingHorizontal: 64,
    textAlign: 'center',
  },
  buttonContainer: {
    width: '60%',
    alignSelf: 'center',
    marginBottom: 48,
  },
  button: {
    height: 64,
  },
});
